import { useState, useMemo, useEffect, useCallback } from "react";
import ModalInDrawer from "../ui/ModalInDrawer";
import { FaTrash, FaPlus, FaInfoCircle, FaWhatsapp } from "react-icons/fa";
import { MdCloudUpload } from "react-icons/md";
import { toast } from "react-toastify";
import axios from "axios";
import { BACKEND } from "../../config/configEnvs";
import countryCodes from "../../constants/countrycodes.json";
import DinamicButton from "../ui/components/DinamicButton";
import { useDropzone } from "react-dropzone";
import DeleteButton from "../ui/components/DeleteButton";
import InputFormValue from "../ui/components/InputFormValue";
import { SiGoogledrive } from "react-icons/si";

export default function ParticipantsModal({ activeModal, onClose, data, rowValues, serviceId, setRowValues, updateCotizacionLocally }) {
  // Find Peru as default country
  const defaultCountry = useMemo(() => {
    return countryCodes.find(country => country.code === "PE") || countryCodes[0];
  }, []);

  // Initialize from rowValues or data
  const initialPhoneNumbers = useMemo(() => {
    const phones = rowValues?.participantNumbers || data?.participantNumbers || [];
    // Ensure phones are in the correct format
    return Array.isArray(phones) ? phones : [];
  }, [rowValues?.participantNumbers, data?.participantNumbers]);

  const initialEmails = useMemo(() => {
    const emails = rowValues?.participantEmails || data?.participantEmails || [];
    // Keep emails as objects if they are objects, convert strings to objects
    if (Array.isArray(emails)) {
      return emails.map(email => {
        if (typeof email === 'string') {
          // Convert string to object format
          return { email: email };
        }
        if (typeof email === 'object' && email !== null) {
          // Keep object structure, ensure it has email property
          return {
            email: email.email || email.value || String(email),
            id: email.id // Preserve id if it exists
          };
        }
        return { email: String(email) };
      });
    }
    return [];
  }, [rowValues?.participantEmails, data?.participantEmails]);

  const initialGroupImage = useMemo(() => {
    return rowValues?.groupImage || data?.groupImage || null;
  }, [rowValues?.groupImage, data?.groupImage]);

  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [emails, setEmails] = useState([]);
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [groupImage, setGroupImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [isCreating, setIsCreating] = useState(false);

  // Load initial values when modal opens or data changes
  useEffect(() => {
    if (activeModal) {
      setPhoneNumbers(initialPhoneNumbers || []);
      setEmails(initialEmails || []);
      setGroupImage(initialGroupImage);
    }
  }, [activeModal, initialPhoneNumbers, initialEmails, initialGroupImage]);

  const servicioName = data?.servicio?.name || "N/A";
  const clienteName = data?.cotizacion?.lead?.leadName || data?.cotizacion?.cliente?.nombreComercial || "";
  
  // Get group and folder creation status and IDs
  const groupCreated = rowValues?.groupCreated || data?.groupCreated || false;
  const folderCreated = rowValues?.folderCreated || data?.folderCreated || false;
  const groupId = rowValues?.groupId || data?.groupId || "";
  const folderId = rowValues?.folderId || data?.folderId || "";

  const automateDisabled = data?.servicio?.needsAutomateSellOps === false

  // Function to get flag emoji from country code
  const getFlagEmoji = (countryCode) => {
    if (!countryCode || typeof countryCode !== 'string') {
      return '🏳️'; // Default flag if countryCode is invalid
    }
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const validatePhone = (phone, countryCode) => {
    // Remove any spaces or special characters
    const cleaned = phone.replace(/\D/g, "");
    // Remove the + from dial_code for comparison
    const dialCode = countryCode.dial_code.replace(/\D/g, "");
    // Check if it starts with the country code and has at least 3 more digits
    return cleaned.startsWith(dialCode) && cleaned.length >= dialCode.length + 3;
  };

  const validateEmail = (email) => {
    // Check if it's a valid email and ends with @gmail.com
    const emailRegex = /^[^\s@]+@gmail\.com$/i;
    return emailRegex.test(email);
  };

  const saveParticipants = async (updatedPhones, updatedEmails) => {
    try {
      // Ensure phones are objects with proper structure
      const normalizedPhones = updatedPhones.map((phone) => {
        if (typeof phone === 'string') {
          return {
            number: phone,
            country: 'PE',
            dialCode: '51',
            display: phone
          };
        }
        return {
          number: phone.number || phone,
          country: phone.country || 'PE',
          dialCode: phone.dialCode || '51',
          display: phone.display || phone.number || phone
        };
      });

      // Ensure emails are objects with email property (backend expects objects)
      const normalizedEmails = updatedEmails.map((email) => {
        if (typeof email === 'string') {
          // Convert string to object format {email: "..."}
          return { email: email };
        }
        // If it's already an object, ensure it has email property
        if (typeof email === 'object' && email !== null) {
          return {
            email: email.email || email.value || String(email),
            id: email.id // Preserve id if it exists (for existing emails)
          };
        }
        return { email: String(email) };
      });

      console.log(normalizedPhones)
      console.log(normalizedEmails)

      // Update rowValues locally with normalized data
      setRowValues((prev) => ({
        ...prev,
        participantNumbers: normalizedPhones,
        participantEmails: normalizedEmails
      }));

      // Save to backend - send as arrays of strings/objects
      const response = await axios.put(
        `${BACKEND}/api/serviciosCotizaciones/${serviceId}`,
        {
          participantNumbers: normalizedPhones,
          participantEmails: normalizedEmails
        }
      );

      // Update local state
      if (updateCotizacionLocally) {
        updateCotizacionLocally(response.data.doc);
      }
    } catch (error) {
      console.log(error)
      console.error("Error al guardar participantes:", error);
      toast.error("Error al guardar los participantes");
    }
  };

  const handleAddPhone = async () => {
    if (groupCreated) {
      toast.error("No se pueden agregar números después de crear el grupo de WhatsApp");
      return;
    }
    
    const cleaned = newPhone.replace(/\D/g, "");
    const dialCode = selectedCountry.dial_code.replace(/\D/g, "");
    
    // If the number doesn't start with the country code, prepend it
    let fullNumber = cleaned;
    if (!cleaned.startsWith(dialCode)) {
      fullNumber = dialCode + cleaned;
    }
    
    if (validatePhone(fullNumber, selectedCountry)) {
      // Store with dial code format (without + sign)
      const newPhoneObj = {
        number: fullNumber,
        country: selectedCountry.code,
        dialCode: dialCode, // Store without + sign
        display: `${dialCode} ${cleaned}`
      };
      
      const updatedPhones = [...phoneNumbers, newPhoneObj];
      setPhoneNumbers(updatedPhones);
      setNewPhone("");
      
      // Save to backend
      await saveParticipants(updatedPhones, emails);
    } else {
      toast.error(`El número debe empezar con ${dialCode}`);
    }
  };

  const handleAddEmail = async () => {
    if (folderCreated) {
      toast.error("No se pueden agregar emails después de crear el folder de Drive");
      return;
    }
    
    if (validateEmail(newEmail)) {
      // Add email as object with email property
      const emailObj = { email: newEmail.toLowerCase() };
      const updatedEmails = [...emails, emailObj];
      setEmails(updatedEmails);
      setNewEmail("");
      
      // Save to backend
      await saveParticipants(phoneNumbers, updatedEmails);
    } else {
      toast.error("El email debe ser una dirección de Gmail válida (ejemplo: usuario@gmail.com)");
    }
  };

  const handleRemovePhone = async (index) => {
    if (groupCreated) {
      toast.error("No se pueden eliminar números después de crear el grupo de WhatsApp");
      return;
    }
    
    const updatedPhones = phoneNumbers.filter((_, i) => i !== index);
    setPhoneNumbers(updatedPhones);
    
    // Save to backend
    await saveParticipants(updatedPhones, emails);
  };

  const handleRemoveEmail = async (index) => {
    if (folderCreated) {
      toast.error("No se pueden eliminar emails después de crear el folder de Drive");
      return;
    }
    
    const updatedEmails = emails.filter((_, i) => i !== index);
    setEmails(updatedEmails);
    
    // Save to backend
    await saveParticipants(phoneNumbers, updatedEmails);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    const numbersOnly = value.replace(/\D/g, "");
    setNewPhone(numbersOnly);
  };

  // Upload group image
  const uploadGroupImage = useCallback(async (file) => {
    if (!file || !serviceId) return;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('servicioCotizacion', serviceId);

      const response = await axios.post(
        `${BACKEND}/api/imagenesServiciosCotizaciones`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      const uploaded = response.data?.doc || response.data;
      if (uploaded?.id) {
        // Update servicioCotizacion with the image id
        await axios.put(`${BACKEND}/api/serviciosCotizaciones/${serviceId}`, {
          groupImage: uploaded.id
        });

        setGroupImage(uploaded);
        
        // Update local state
        setRowValues((prev) => ({
          ...prev,
          groupImage: uploaded
        }));

        if (updateCotizacionLocally) {
          updateCotizacionLocally({ id: serviceId, groupImage: uploaded });
        }

        toast.success('Imagen del grupo subida correctamente');
      } else {
        throw new Error('Respuesta inesperada al subir imagen');
      }
    } catch (error) {
      console.error('Error al subir imagen:', error);
      toast.error('Error al subir la imagen');
    } finally {
      setUploadingImage(false);
    }
  }, [serviceId, setRowValues, updateCotizacionLocally]);

  // Delete group image
  const eliminarGroupImage = async () => {
    if (groupCreated) {
      toast.error("No se puede eliminar la imagen después de crear el grupo de WhatsApp");
      return;
    }
    
    try {
      const confirm = window.confirm('¿Estás seguro de querer eliminar la imagen del grupo?');
      if (!confirm) {
        return;
      }

      if (groupImage?.id) {
        await axios.delete(`${BACKEND}/api/imagenesServiciosCotizaciones/${groupImage.id}`);
      }

      // Update servicioCotizacion to remove the image reference
      await axios.put(`${BACKEND}/api/serviciosCotizaciones/${serviceId}`, {
        groupImage: null
      });

      setGroupImage(null);
      
      // Update local state
      setRowValues((prev) => ({
        ...prev,
        groupImage: null
      }));

      if (updateCotizacionLocally) {
        updateCotizacionLocally({ id: serviceId, groupImage: null });
      }

      toast.success('Imagen del grupo eliminada');
    } catch (error) {
      console.error('Error al eliminar imagen:', error);
      toast.error('Error al eliminar la imagen');
    }
  };

  // Dropzone for group image
  const onDropImage = useCallback((acceptedFiles) => {
    if (groupImage || uploadingImage) {
      toast.info('Ya existe una imagen. Elimina la actual para subir otra.');
      return;
    }
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    uploadGroupImage(file);
  }, [groupImage, uploadingImage, uploadGroupImage]);

  const { getRootProps: getRootPropsImage, getInputProps: getInputPropsImage, isDragActive: isDragActiveImage } = useDropzone({
    onDrop: onDropImage,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif'] },
    multiple: false,
    disabled: Boolean(groupImage || uploadingImage || groupCreated || automateDisabled)
  });

  const AutomateSellOps = async () => {
    const toastId = toast.loading("Creando grupo de WhatsApp y Drive...")
    try {
      setIsCreating(true);
      const response = await axios.post(`${BACKEND}/api/automateSellOps`, {
        serviceId: serviceId,
      });
      
      // Try to get the updated service to get the IDs
      let updatedServiceData = {
        id: serviceId,
        groupId: response.data?.data?.groupId,
        folderId: response.data?.data?.folderId,
        groupCreated: response.data?.data?.groupCreated,
        folderCreated: response.data?.data?.folderCreated,
      };
      
      // If IDs are not in response, try to fetch the updated service
      // if (!updatedServiceData.groupId || !updatedServiceData.folderId) {
      //   try {
      //     const serviceResponse = await axios.get(`${BACKEND}/api/serviciosCotizaciones/${serviceId}`);
      //     if (serviceResponse.data?.doc) {
      //       updatedServiceData.groupId = serviceResponse.data.doc.groupId || updatedServiceData.groupId;
      //       updatedServiceData.folderId = serviceResponse.data.doc.folderId || updatedServiceData.folderId;
      //       updatedServiceData.groupCreated = serviceResponse.data.doc.groupCreated || updatedServiceData.groupCreated;
      //       updatedServiceData.folderCreated = serviceResponse.data.doc.folderCreated || updatedServiceData.folderCreated;
      //     }
      //   } catch (fetchError) {
      //     console.log("Error fetching updated service:", fetchError);
      //   }
      // }
      
      // Update rowValues locally
      setRowValues((prev) => ({
        ...prev,
        groupCreated: true,
        folderCreated: true,
        groupId: updatedServiceData.groupId,
        folderId: updatedServiceData.folderId
      }));
      
      // Update cotización locally using the callback
      if (updateCotizacionLocally) {
        updateCotizacionLocally(updatedServiceData);
      }
      
      toast.update(toastId, {
        render: response.data.message || "Grupo de WhatsApp y Drive creados exitosamente",
        type: "success",
        isLoading: false,
        autoClose: 3000
      });
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Error desconocido";
      toast.update(toastId, {
        render: "Error al crear el grupo de WhatsApp y Drive: " + errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 3000
      });
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <ModalInDrawer
      activeModal={activeModal}
      onClose={onClose}
      title={`Operaciones de venta - ${servicioName}${clienteName ? ` - ${clienteName}` : ""}`}
      className="max-w-7xl"
      centered
    >
      <div className="flex flex-col gap-4 text-xs">

        <span className="text-xs text-gray-500">
          <FaInfoCircle className="inline-block mr-1" />
          Los números de teléfono y emails acá colocados serán invitados al grupo de Whatsapp y al Drive del servicio, los cuales se crearán automáticamente a los 2 días a partir de la fecha de venta, 
          siempre y cuando el vendedor <span className="font-bold">no</span> haya activado antes el proceso manualmente (Dandole click al botón de "Crear grupo de WhatsApp y Drive"). Esto <span className="font-bold">siempre y cuando haya una imagen del grupo cargada.</span>
        </span>

        {/* Two column layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Left column - Image upload (1/3 width) */}
          <div className="border border-gray-300 rounded-md p-4 md:col-span-1">
            <label className="block text-gray-700 mb-2">
              Imagen del Grupo
            </label>
            <div 
              {...(!groupImage && !uploadingImage && !groupCreated && !automateDisabled ? getRootPropsImage() : {})}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                uploadingImage ? 'border-blue-500 bg-blue-50' 
                : groupCreated || automateDisabled ? 'border-gray-300 bg-gray-100 cursor-not-allowed opacity-60' 
                : groupImage ? 'border-gray-300 hover:border-gray-400' 
                : isDragActiveImage ? 'border-blue-500 bg-blue-50 cursor-pointer' 
                : 'border-gray-300 hover:border-gray-400 cursor-pointer'
              }`}
            >
              {!groupImage && !uploadingImage && !groupCreated && !automateDisabled && <input {...getInputPropsImage()} />}
              {uploadingImage ? (
                <div className="space-y-4">
                  <div className="mx-auto h-12 w-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-gray-400">Subiendo imagen...</p>
                </div>
              ) : groupImage ? (
                <div className="space-y-2">
                  <div className="relative w-full">
                    <img 
                      src={typeof groupImage === 'object' ? groupImage.url : groupImage} 
                      alt="Imagen del grupo"
                      className="w-full h-auto rounded-lg object-contain"
                    />
                    {!groupCreated && (
                      <div className="absolute top-2 right-2">
                        <DeleteButton
                          action={eliminarGroupImage}
                          content="Eliminar imagen"
                        />
                      </div>
                    )}
                  </div>
                  {typeof groupImage === 'object' && groupImage.filename && (
                    <p className="text-xs text-gray-500 truncate">{groupImage.filename}</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <MdCloudUpload className="mx-auto h-10 w-10 text-gray-400" />
                  <p className="text-sm text-gray-600">
                    Arrastra y suelta la <b className="text-blue-500">imagen del grupo</b>, o haz clic para seleccionar
                  </p>
                  <p className="text-xs text-gray-500">Solo imágenes (máx. 10MB)</p>
                </div>
              )}
            </div>
          </div>

          {/* Right column - Participants (2/3 width) */}
          <div className="flex flex-col gap-4 md:col-span-2">
            {/* Lista de Números de Teléfono */}
            <div className="border border-gray-300 rounded-md p-4">
              <label className="block text-gray-700 mb-2" htmlFor="phoneNumbers">
                Números de Teléfono
              </label>

              <div className="flex flex-col w-full">
                {phoneNumbers.length > 0 ? (
                  <ul className="list-disc">
                    {phoneNumbers.map((phoneObj, index) => {
                      const phone = typeof phoneObj === 'string' ? phoneObj : phoneObj.display || phoneObj.number;
                      const countryCode = typeof phoneObj === 'object' && phoneObj ? (phoneObj.country || 'PE') : 'PE';
                      return (
                        <div key={index} className="mb-4">
                          <div className="space-y-4 bg-gray-50 border border-gray-200 rounded-md p-2 shadow-sm">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{getFlagEmoji(countryCode)}</span>
                                <span className="font-semibold text-gray-800">{phone}</span>
                              </div>
                              {!groupCreated && (
                                <div className="flex space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => handleRemovePhone(index)}
                                    className="p-2 text-red-500 hover:bg-red-600 hover:bg-opacity-20 rounded-md transition duration-150"
                                    title="Eliminar número"
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="flex justify-center items-center h-[50px] border rounded-md mb-4">
                    No hay números agregados
                  </div>
                )}
              </div>

              <div>
                <div className="flex">
                  <div className="relative">
                    <span className="text-xs inline-flex items-center px-3 text-gray-900 bg-gray-200 border rounded-e-0 border-gray-300 border-e-0 rounded-s-md h-[42px]">
                      <span className="text-base">{getFlagEmoji(selectedCountry.code)}</span>
                    </span>
                    <select
                      value={selectedCountry.code}
                      onChange={(e) => {
                        const country = countryCodes.find(c => c.code === e.target.value);
                        if (country) setSelectedCountry(country);
                      }}
                      disabled={groupCreated || automateDisabled}
                      className={`absolute inset-0 w-full h-full opacity-0 z-10 text-xs ${
                        groupCreated || automateDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                      }`}
                      title={`${selectedCountry.name} ${selectedCountry.dial_code}`}
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.dial_code} {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <input
                    type="text"
                    value={newPhone}
                    onChange={handlePhoneChange}
                    placeholder={`${selectedCountry.dial_code.replace('+', '')}XXXXXXXXX`}
                    disabled={groupCreated || automateDisabled}
                    className={`text-xs rounded-l-none rounded-r-lg border block flex-1 min-w-0 w-full text-sm border-gray-300 p-2.5 ${
                      groupCreated || automateDisabled 
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                        : 'bg-gray-50 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !groupCreated && !automateDisabled) {
                        handleAddPhone();
                      }
                    }}
                  />

                  
                  <button
                    onClick={handleAddPhone}
                    disabled={groupCreated || automateDisabled}
                    className={`ml-2 flex justify-center items-center gap-2 transition-all duration-100 px-4 py-2 rounded-lg border whitespace-nowrap h-[42px] ${
                      groupCreated || automateDisabled 
                        ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-60' 
                        : 'bg-white hover:bg-blue-200 border-gray-300'
                    }`}
                    type="button"
                  >
                    <FaPlus className={groupCreated || automateDisabled ? "text-gray-500" : "text-blue-700"} />
                    <span className={groupCreated || automateDisabled ? "text-gray-500 text-xs" : "text-blue-700 text-xs"}>Agregar numero</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Lista de Emails */}
            <div className="border border-gray-300 rounded-md p-4">
              <label className="block text-gray-700 mb-2" htmlFor="emails">
                Emails Gmail
              </label>
              <div className="flex flex-col w-full">
                {emails.length > 0 ? (
                  <ul className="list-disc">
                    {emails.map((email, index) => {
                      // Extract email string from object
                      const emailString = typeof email === 'string' 
                        ? email 
                        : (typeof email === 'object' && email !== null 
                            ? (email.email || email.value || String(email))
                            : String(email));
                      
                      return (
                        <div key={index} className="mb-4">
                          <div className="space-y-4 bg-gray-50 border border-gray-200 rounded-md p-2 shadow-sm">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-gray-800">{emailString}</span>
                              </div>
                              {!folderCreated && (
                                <div className="flex space-x-2">
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveEmail(index)}
                                    className="p-2 text-red-500 hover:bg-red-600 hover:bg-opacity-20 rounded-md transition duration-150"
                                    title="Eliminar email"
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="flex justify-center items-center h-[50px] border rounded-md mb-4">
                    No hay emails agregados
                  </div>
                )}
              </div>

              <div>
                <div className="flex">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="usuario@gmail.com"
                    disabled={folderCreated || automateDisabled}
                    className={`text-xs rounded-lg border block flex-1 min-w-0 w-full text-sm border-gray-300 p-2.5 ${
                      folderCreated || automateDisabled 
                        ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                        : 'bg-gray-50 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
                    }`}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !folderCreated && !automateDisabled) {
                        handleAddEmail();
                      }
                    }}
                  />
                  <button
                    onClick={handleAddEmail}
                    disabled={folderCreated || automateDisabled}
                    className={`ml-2 flex justify-center items-center gap-2 transition-all duration-100 px-4 py-2 rounded-lg border whitespace-nowrap h-[42px] ${
                      folderCreated || automateDisabled 
                        ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-60' 
                        : 'bg-white hover:bg-blue-200 border-gray-300'
                    }`}
                    type="button"
                  >
                    <FaPlus className={folderCreated || automateDisabled ? "text-gray-500" : "text-blue-700"} />
                    <span className={folderCreated || automateDisabled ? "text-gray-500 text-xs" : "text-blue-700 text-xs"}>Agregar gmail</span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">Solo se permiten direcciones de <span className="text-red-500 font-bold">Gmail</span></p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full mt-4 space-y-3">
          {/* Show IDs if group and/or folder are created */}
          {(groupCreated || folderCreated) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border border-gray-300 rounded-md p-4 bg-gray-50">
              {groupCreated && (
                <InputFormValue
                  title="ID del Grupo de WhatsApp"
                  value={groupId}
                  setValue={() => {}}
                  type="text"
                  disabled={true}
                  icon={<FaWhatsapp className="text-green-500" />}
                />
              )}
              {folderCreated && (
                  <InputFormValue
                    title="URL del Folder de Drive"
                    value={folderId ? `https://drive.google.com/drive/folders/${folderId}` : ""}
                    setValue={() => {}}
                    type="text"
                    disabled={true}
                    icon={<SiGoogledrive className="text-blue-500" />}
                    onClick={() => {
                      if (folderId) {
                        window.open(`https://drive.google.com/drive/folders/${folderId}`, "_blank", "noopener,noreferrer");
                      }
                    }}
                  />
              )}
            </div>
          )}
          {automateDisabled && (
            <p className="text-xs text-red-500 mb-2 flex items-center gap-1">
              <FaInfoCircle className="inline-block" />
              Las operaciones automáticas de WhatsApp y Drive están desactivadas para este servicio.
            </p>
          )}
          {!groupImage && !(groupCreated && folderCreated) && !automateDisabled && (
            <p className="text-xs text-amber-600 mb-2 flex items-center gap-1">
              <FaInfoCircle className="inline-block" />
              Debes subir una imagen del grupo antes de crear el grupo de WhatsApp y Drive
            </p>
          )}
          {groupCreated && folderCreated && (
            <p className="text-xs text-green-600 mb-2 flex items-center gap-1">
              <FaInfoCircle className="inline-block" />
              El grupo de WhatsApp y Drive ya han sido creados
            </p>
          )}
          <DinamicButton
            content="Crear grupo de WhatsApp y Drive"
            action={AutomateSellOps}
            width="100%"
            backgroundColor="#2563eb"
            hoverBackgroundColor="#1d4ed8"
            fontSize="12px"
            disabled={
              !groupImage || (groupCreated && folderCreated) || (data?.servicio?.needsAutomateSellOps === false) || isCreating
            }
          />
        </div>
      </div>
    </ModalInDrawer>
  );
}